/**
 * Review Prompt Service
 *
 * Manages the "Are you enjoying enough?" pre-prompt that gates the native
 * app store review (iOS SKStoreReviewController / Android Play In-App Review).
 *
 * State lives on `uservars/{uid}.reviewPrompt`. Because neither OS API tells
 * us whether the sheet rendered or what rating was left, "we asked the OS"
 * (status: 'completed') is treated as the terminal success state.
 *
 * Eligibility = native platform + feature flag on + enough activity days
 * (checked by the caller) AND a not-recently-asked / not-finished state
 * (checked here via `isReviewPromptEligible`).
 */

import {
  doc,
  onSnapshot,
  setDoc,
  increment,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ReviewPromptState } from '../types';

// ============================================
// TUNABLES
// ============================================

/** Lifetime cap on how many times we'll show the pre-prompt to one user. */
export const REVIEW_PROMPT_MAX_PROMPTS = 3;
/** After a silent dismiss, wait this long before re-asking. */
export const REVIEW_PROMPT_SNOOZE_DAYS = 14;
/** After "Not really" + feedback, wait this long (gives us time to improve). */
export const REVIEW_PROMPT_NOT_ENJOYING_SNOOZE_DAYS = 90;

// ============================================
// REFERENCES / HELPERS
// ============================================

const getUservarsRef = (uid: string) => doc(db, 'uservars', uid);
const nowIso = (): string => new Date().toISOString();
const isoInDays = (days: number): string =>
  new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

// ============================================
// READ / SUBSCRIBE
// ============================================

/** Real-time subscription to the user's review-prompt state (null if never asked). */
export const subscribeToReviewPromptState = (
  uid: string,
  callback: (state: ReviewPromptState | null) => void
): Unsubscribe =>
  onSnapshot(
    getUservarsRef(uid),
    (snap) => callback((snap.data()?.reviewPrompt as ReviewPromptState) ?? null),
    (error) => {
      console.error('Error subscribing to review prompt state:', error);
      callback(null);
    }
  );

// ============================================
// ELIGIBILITY
// ============================================

/**
 * Whether the user is in a state where we're allowed to show the pre-prompt.
 * Does NOT check platform, feature flag, or activity-day threshold — the
 * caller layers those on top.
 */
export const isReviewPromptEligible = (
  state: ReviewPromptState | null,
  nowMs: number = Date.now()
): boolean => {
  if (!state) return true; // never asked
  if (state.status === 'completed' || state.status === 'enjoying') return false; // already reviewed
  if (state.promptCount >= REVIEW_PROMPT_MAX_PROMPTS) return false;
  if (state.snoozeUntil && nowMs < new Date(state.snoozeUntil).getTime()) return false;
  return true;
};

// ============================================
// MUTATIONS
// ============================================

/** Mark that the pre-prompt was shown (increments count, stamps timestamps). */
export const markReviewPromptShown = async (
  uid: string,
  isFirstEver: boolean
): Promise<void> => {
  const reviewPrompt: Record<string, unknown> = {
    status: 'shown',
    promptCount: increment(1),
    lastShownAt: nowIso(),
  };
  if (isFirstEver) reviewPrompt.firstShownAt = nowIso();
  await setDoc(getUservarsRef(uid), { reviewPrompt }, { merge: true });
};

/** User said "Yes, I'm enjoying it" and we triggered the native review sheet. */
export const markReviewCompleted = async (uid: string): Promise<void> => {
  await setDoc(
    getUservarsRef(uid),
    { reviewPrompt: { status: 'completed', outcome: 'enjoying' } },
    { merge: true }
  );
};

/** User said "Not really" — record outcome and snooze for a long window. */
export const markNotEnjoying = async (uid: string): Promise<void> => {
  await setDoc(
    getUservarsRef(uid),
    {
      reviewPrompt: {
        status: 'not_enjoying',
        outcome: 'not_enjoying',
        snoozeUntil: isoInDays(REVIEW_PROMPT_NOT_ENJOYING_SNOOZE_DAYS),
      },
    },
    { merge: true }
  );
};

/** User dismissed the pre-prompt without choosing — try again later. */
export const snoozeReviewPrompt = async (uid: string): Promise<void> => {
  await setDoc(
    getUservarsRef(uid),
    {
      reviewPrompt: {
        status: 'snoozed',
        snoozeUntil: isoInDays(REVIEW_PROMPT_SNOOZE_DAYS),
      },
    },
    { merge: true }
  );
};
